import type { DerivedStats, DieSides, GameState, MachineId, MasterDie, Rarity } from './types';
import { namedAffixOf } from './dice';
import { namedDieName } from '../data/affixes';
import {
  bestDie,
  isMachineUnlocked,
  isValidLot,
  lotCost,
  machineById,
  pullLot,
  recycleValue,
  sortByRarity,
} from './gacha';
import type { Rng } from './rng';
import { DEFAULT_BALANCE, type ResolvedBalance } from './balance';
import { corruptedLotCost } from './economy';
import { canBuyNode, nodeLevel } from './skilltree';

/**
 * Opérations d'Accalmie (arbre, Gachapon, inventaire).
 * Ces fonctions mutent l'état passé en paramètre et ne connaissent ni les
 * événements ni le DOM : la façade `Game` les enrobe pour émettre les events.
 */

/** Ajuste la taille du tableau d'emplacements au nombre de slots débloqués. */
export function syncSlots(state: GameState, slots: number): void {
  const eq = state.equipped;
  while (eq.length < slots) eq.push(null);
  if (eq.length > slots) eq.length = slots;
}

/**
 * Achète un niveau de nœud. Renvoie le coût payé, ou 0 si l'achat est refusé.
 * Les portes de l'Abîme lisent `state.stats.bestLevel` (A94-3).
 */
export function buyNode(state: GameState, id: string, bal: ResolvedBalance = DEFAULT_BALANCE): number {
  const check = canBuyNode(state.nodes, state.gold, id, bal, state.stats.bestLevel);
  if (!check.ok) return 0;
  state.gold -= check.cost;
  state.nodes[id] = nodeLevel(state.nodes, id) + 1;
  return check.cost;
}

export interface GachaOutcome {
  dice: MasterDie[];
  cost: number;
}

/**
 * Prix d'un lot à une *Corrupted GachaDice* (A94-13, A101) : somme des prix
 * unitaires successifs à partir du prix de base fixe (`corruptedBaseCost`),
 * compteur de la machine (`state.corruptedPurchases`, remis à zéro à la mort).
 * `bal` est gardé pour la signature (le prix ne dépend plus d'Exp).
 */
export function corruptedLotCostFor(
  state: GameState,
  machine: MachineId,
  qty: number,
  stats: DerivedStats,
  _bal: ResolvedBalance = DEFAULT_BALANCE,
): number {
  const purchases = state.corruptedPurchases?.[machine] ?? 0;
  return corruptedLotCost(purchases, qty, stats.gachaDiscountLevel);
}

/**
 * Ouvre un lot au Gachapon et range les dés dans l'inventaire.
 *
 * Les dés **renvoyés** sont triés par rareté décroissante (COMMENTS v0.6.0 §2) ;
 * l'inventaire, lui, garde l'ordre de tirage.
 *
 * v0.46.0 (A94-13) : une *Corrupted GachaDice* (verrouillée tant qu'*Artisan
 * des dés* vaut 0, ouverte ensuite à tous les niveaux) facture la somme des
 * prix unitaires du lot, fait avancer le compteur permanent de la machine d'un
 * par dé acheté et donne des dés à niveau et Matériau.
 */
export function buyGacha(
  state: GameState,
  machine: MachineId,
  qty: number,
  stats: DerivedStats,
  rng: Rng,
  bal: ResolvedBalance = DEFAULT_BALANCE,
): GachaOutcome | null {
  const def = machineById(machine);
  if (!def || !isValidLot(qty)) return null;
  if (!isMachineUnlocked(state.nodes, def)) return null;
  const corrupted = def.corrupted === true;
  const cost = corrupted
    ? corruptedLotCostFor(state, machine, qty, stats, bal)
    : lotCost(def, qty, stats, bal);
  if (state.gold < cost) return null;
  state.gold -= cost;
  const { dice } = pullLot(def, qty, stats, rng, bal, corrupted ? { corrupted: true, levels: state.nodes } : undefined);
  if (corrupted) {
    state.corruptedPurchases ??= {};
    state.corruptedPurchases[machine] = (state.corruptedPurchases[machine] ?? 0) + qty;
  }
  state.inventory.push(...dice);
  return { dice: sortByRarity(dice), cost };
}

/** Verdict d'équipement : `ok`, ou la raison du refus en français. */
export interface EquipCheck {
  ok: boolean;
  reason?: string;
}

/**
 * Peut-on poser `dieId` dans `slot` ? Refus (A72-14) quand le dé porte un
 * affixe **nommé** (Guinsoo, Chef de chantier, ArchiDicer) déjà porté par un
 * **autre** dé qui resterait équipé après l'opération : un seul exemplaire de
 * chaque dé nommé équipé à la fois. Remplacer un Guinsoo par un autre Guinsoo
 * dans le même emplacement reste permis.
 */
export function canEquipDie(state: GameState, dieId: string, slot: number): EquipCheck {
  if (slot < 0 || slot >= state.equipped.length) return { ok: false, reason: 'Emplacement inconnu.' };
  const die = state.inventory.find((d) => d.id === dieId);
  if (!die) return { ok: false, reason: 'Dé introuvable.' };
  const named = namedAffixOf(die);
  if (!named) return { ok: true };
  const current = state.equipped.indexOf(dieId);
  const previous = state.equipped[slot];
  // Équipement **après** l'opération (échange compris).
  const after = state.equipped.slice();
  if (current >= 0) after[current] = previous;
  after[slot] = dieId;
  for (let i = 0; i < after.length; i++) {
    const id = after[i];
    if (!id || id === dieId) continue;
    const other = state.inventory.find((d) => d.id === id);
    if (namedAffixOf(other) === named) {
      return { ok: false, reason: `Un seul ${namedDieName(named) ?? die.name} équipé à la fois.` };
    }
  }
  return { ok: true };
}

/** Équipe un dé dans un emplacement (échange si le dé est déjà équipé ailleurs). */
export function equipDie(state: GameState, dieId: string, slot: number): boolean {
  if (!canEquipDie(state, dieId, slot).ok) return false;
  const current = state.equipped.indexOf(dieId);
  const previous = state.equipped[slot];
  if (current >= 0) state.equipped[current] = previous;
  state.equipped[slot] = dieId;
  return true;
}

export function unequipDie(state: GameState, slot: number): boolean {
  if (slot < 0 || slot >= state.equipped.length) return false;
  if (!state.equipped[slot]) return false;
  state.equipped[slot] = null;
  return true;
}

/** Le Recycleur est-il débloqué ? */
export function canRecycle(state: GameState): boolean {
  return nodeLevel(state.nodes, 'shop_recycle') >= 1;
}

/** Revend un dé non équipé. Renvoie l'or obtenu (0 si refusé). */
export function recycleDie(state: GameState, dieId: string): number {
  if (!canRecycle(state)) return 0;
  if (state.equipped.includes(dieId)) return 0;
  const index = state.inventory.findIndex((d) => d.id === dieId);
  if (index < 0) return 0;
  const value = recycleValue(state.inventory[index]);
  state.inventory.splice(index, 1);
  state.gold += value;
  state.stats.totalGold += value;
  return value;
}

/**
 * Filtre de recyclage en masse — COMMENTS v0.2.3 §3 (HUD).
 * Chaque champ renseigné doit correspondre (ET logique) ; un filtre vide
 * désigne **tous** les dés non équipés de l'inventaire.
 */
export interface RecycleFilter {
  rarity?: Rarity;
  sides?: DieSides;
  /**
   * v0.55.8 (A119) : plusieurs raretés à la fois (**OU** entre elles, ET avec le reste) ;
   * vide ou absent = toutes. Se compose avec `rarity` (ET).
   */
  rarities?: readonly Rarity[];
  /** Idem pour les faces. */
  sidesList?: readonly DieSides[];
  /** v0.55.8 (A119) : un dé **Enrichi** n'est jamais recyclé par ce filtre. */
  spareEnriched?: boolean;
  /** v0.55.8 (A119) : un dé portant un **Matériau** n'est jamais recyclé par ce filtre. */
  spareMaterial?: boolean;
}

/** Résultat (ou aperçu) d'un recyclage en masse. */
export interface RecycleOutcome {
  count: number;
  gold: number;
}

function matchesFilter(die: MasterDie, filter: RecycleFilter): boolean {
  if (filter.rarity !== undefined && die.rarity !== filter.rarity) return false;
  if (filter.sides !== undefined && die.sides !== filter.sides) return false;
  if (filter.rarities && filter.rarities.length > 0 && !filter.rarities.includes(die.rarity)) return false;
  if (filter.sidesList && filter.sidesList.length > 0 && !filter.sidesList.includes(die.sides)) return false;
  if (filter.spareEnriched && die.enriched === true) return false;
  if (filter.spareMaterial && die.material !== undefined) return false;
  return true;
}

/**
 * Dés effectivement recyclables : non équipés et conformes au filtre.
 * Renvoie une liste vide tant que le Recycleur n'est pas débloqué.
 */
export function recyclableDice(state: GameState, filter: RecycleFilter = {}): MasterDie[] {
  if (!canRecycle(state)) return [];
  return state.inventory.filter(
    (d) => !state.equipped.includes(d.id) && matchesFilter(d, filter),
  );
}

/** Aperçu d'un recyclage en masse : **ne modifie aucun état**. */
export function previewRecycle(state: GameState, filter: RecycleFilter = {}): RecycleOutcome {
  const dice = recyclableDice(state, filter);
  let gold = 0;
  for (const die of dice) gold += recycleValue(die);
  return { count: dice.length, gold };
}

/** Revend tous les dés non équipés correspondant au filtre. */
export function recycleWhere(state: GameState, filter: RecycleFilter = {}): RecycleOutcome {
  const ids = recyclableDice(state, filter).map((d) => d.id);
  let gold = 0;
  let count = 0;
  for (const id of ids) {
    const value = recycleDie(state, id);
    if (value > 0) {
      gold += value;
      count += 1;
    }
  }
  return { count, gold };
}

/** Revend tous les dés Communs non équipés. Raccourci historique de la Boutique. */
export function recycleAllCommon(state: GameState): number {
  return recycleWhere(state, { rarity: 'common' }).gold;
}

/**
 * Remplit tous les emplacements avec les meilleurs dés de l'inventaire. Un dé
 * nommé déjà retenu écarte ses doublons (un seul exemplaire équipé — A72-14).
 */
export function equipBest(state: GameState, stats: DerivedStats): void {
  let pool = state.inventory.slice();
  for (let slot = 0; slot < state.equipped.length; slot++) {
    const best = bestDie(pool, stats);
    if (!best) {
      state.equipped[slot] = null;
      continue;
    }
    state.equipped[slot] = best.id;
    pool.splice(pool.indexOf(best), 1);
    const named = namedAffixOf(best);
    if (named) pool = pool.filter((d) => namedAffixOf(d) !== named);
  }
}
